export interface Rect { x: number; y: number; w: number; h: number }

export interface TopCardRects {
  bandH: number;
  accent: Rect;
  card: Rect;
  photo: Rect;
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/**
 * Геометрия макета top-card в template-space. Чистая функция (без canvas/DOM).
 * Верхняя плашка (accent) на всю ширину; карточка (card) с горизонтальным инсетом;
 * фото заполняет остаток снизу.
 */
export function computeTopCardRects(params: {
  width: number; height: number; bandHeight: number; cardInset: number;
}): TopCardRects {
  const { width, height } = params;
  const frac = clamp(params.bandHeight || 0.22, 0.15, 0.35);
  const bandH = Math.round(height * frac);
  // Инсет не больше чем (width/2 - 20), чтобы карточка осталась видимой.
  const inset = clamp(params.cardInset || 0, 0, Math.floor(width / 2) - 20);
  return {
    bandH,
    accent: { x: 0, y: 0, w: width, h: bandH },
    card: { x: inset, y: 0, w: width - 2 * inset, h: bandH },
    photo: { x: 0, y: bandH, w: width, h: height - bandH },
  };
}

export interface FrameTextBox { cx: number; cy: number; maxWidth: number; maxHeight: number }

/**
 * Геометрия текстового блока для макета frame. Чистая функция.
 * cx/cy — центр (доли X/Y), maxWidth — ширина переноса, maxHeight — лимит высоты (верхняя половина).
 */
export function computeFrameTextBox(params: {
  width: number; height: number; frameTextX: number; frameTextY: number; frameTextWidth: number;
}): FrameTextBox {
  const { width, height } = params;
  const x = clamp(params.frameTextX ?? 0.5, 0, 1);
  const y = clamp(params.frameTextY ?? 0.12, 0, 1);
  const w = clamp(params.frameTextWidth ?? 0.8, 0.1, 1);
  return {
    cx: Math.round(width * x),
    cy: Math.round(height * y),
    maxWidth: Math.round(width * w),
    maxHeight: Math.round(height * 0.5),
  };
}

/**
 * Держит блок заголовка внутри кадра в макете frame.
 *
 * Якорь текста у большинства шаблонов стоит на 0.90–0.94 высоты, а выравнивание
 * по центру — значит половина блока уходит ниже якоря. Короткий заголовок так и
 * задуман, длинный наполовину уезжал за нижний край кадра и обрезался.
 * Кегль при этом не трогаем: блок просто поднимается ровно настолько, чтобы влезть.
 */
export function clampFrameTextY(params: {
  anchoredY: number;
  blockHeight: number;
  canvasHeight: number;
  margin: number;
  /**
   * Верх измеренной плашки каркаса. Если задан, блок не поднимается выше него —
   * иначе заголовок залезает на фото. Без него потолком служит край кадра.
   */
  topLimit?: number;
  /**
   * Низ измеренной плашки. Нужен для плашек, висящих в воздухе (inform_qz):
   * там нижний край кадра — не граница, заголовок обязан кончиться раньше.
   */
  bottomLimit?: number;
}): number {
  const { anchoredY, blockHeight, canvasHeight, margin, topLimit, bottomLimit } = params;
  const lowest = (bottomLimit ?? canvasHeight - margin) - blockHeight;
  const desired = topLimit ?? margin;
  // Блок не помещается между верхом плашки и низом кадра — прижимать его к
  // плашке нельзя, он уедет за нижний край и обрежется. Пусть лучше вылезет
  // вверх: там его хотя бы видно.
  const highest = lowest >= desired ? desired : margin;
  return Math.max(highest, Math.min(anchoredY, lowest));
}

/**
 * Оценка доступной заголовку высоты по якорю — запасной путь.
 *
 * Плашка в макете frame нарисована внутри PNG-каркаса, её высота нигде не
 * записана. Оценка исходит из того, что дизайнер ставит якорь в центр плашки,
 * поэтому симметричное расстояние от якоря до ближайшего края кадра примерно
 * равно её высоте.
 *
 * Допущение верно не всегда: сверка с 41 каркасом дала ошибку до +38 px у 13
 * штук, а у ortalyq.news (текст сверху, плашка снизу) — +1027. Поэтому там,
 * где плашку удаётся измерить в самом PNG, используется измерение
 * (см. measureFrameTextSpace в shared/lib/frameBand), а эта оценка остаётся
 * запасным путём для каркасов с
 * градиентом вместо плашки, где мерить нечего.
 */
export function frameTextAvailableHeight(params: {
  cy: number;
  canvasHeight: number;
  vAlign: 'top' | 'center' | 'bottom';
  margin: number;
}): number {
  const { cy, canvasHeight, vAlign, margin } = params;
  const raw =
    vAlign === 'bottom' ? cy
    : vAlign === 'top' ? canvasHeight - cy
    : 2 * Math.min(cy, canvasHeight - cy);
  // Не даём выродиться в ноль: якорь у самого края — всё равно нужен минимум места.
  return Math.max(margin * 2, Math.min(raw, canvasHeight - margin * 2));
}

/**
 * Ставит блок строк по центру измеренной плашки.
 *
 * Раньше блок ставился по якорю шаблона и потом зажимался в границы плашки
 * (clampFrameTextY). У большинства каркасов якорь стоит ниже её середины,
 * поэтому текст прижимался к низу; а когда блок не помещался, зажим выталкивал
 * его целиком ВЫШЕ плашки — заголовок уезжал на фотографию, где его почти не
 * видно, и при этом сама плашка снизу оставалась пустой.
 *
 * Теперь: влезает — стоит по центру плашки; не влезает — вылезает поровну
 * вверх и вниз, оставаясь на плашке. За край кадра не пускаем в любом случае.
 */
export function centerBlockInBand(params: {
  /** Верх измеренной плашки, render-space px. */
  bandTop: number;
  /** Низ измеренной плашки включительно, render-space px. */
  bandBottom: number;
  /** Высота рамки строк (кегль × межстрочный × число строк). */
  blockHeight: number;
  /**
   * Насколько ниже верха рамки начинаются сами буквы. У первой строки над
   * заглавными всегда есть пустой зазор — он часть кегля, а не текста.
   */
  inkTop: number;
  /** Высота собственно букв: от верха первой строки до низа последней. */
  inkHeight: number;
  canvasHeight: number;
}): number {
  const { bandTop, bandBottom, blockHeight, inkTop, inkHeight, canvasHeight } = params;
  // Центрируем буквы, а не рамку строк. Разница заметная: у рамки снизу
  // остаётся место под выносные элементы («р», «у»), которых в последней
  // строке может и не быть, и текст выглядит приклеенным к верху плашки.
  const centered = (bandTop + bandBottom) / 2 - inkHeight / 2 - inkTop;
  // Единственная граница — край кадра: обрезанный текст хуже смещённого.
  return Math.max(0, Math.min(centered, canvasHeight - blockHeight));
}
